import { useState, useEffect } from 'react';
import { useDirectGameSession } from '@/hooks/useDirectGameSession';
import { NativeChessBoard } from '@/components/NativeChessBoard';
import { CapturedPieces } from '@/components/CapturedPieces';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Loader2, Clock, AlertCircle, Trophy, Wifi, RotateCcw, Check, X, Activity, ArrowLeft } from 'lucide-react';
import { Position, PieceType, getAllLegalMoves, getGameStatus } from '@/lib/chess-engine';
import { normalizeGameState } from '@/lib/gameStateUtils';
import { usePlayerProfiles } from '@/hooks/usePlayerProfiles';
import { useLocalClock } from '@/hooks/useLocalClock';
import { useCosmetics } from '@/hooks/useCosmetics';
import { formatTime } from '@/lib/clock-utils';
import { AvatarDisplay } from './AvatarDisplay';
import { getPieceImages, type PieceStyle } from '@/lib/pieces';
import { useToast } from '@/hooks/use-toast';
import { useAuth } from '@/hooks/useAuth';
import { EloNotification } from './EloNotification';
import { useCurrency } from '@/hooks/useCurrency';
import { EloPredictor } from './EloPredictor';
import { GameReviewPanel } from './GameReviewPanel';
import { useIsMobile } from '@/hooks/use-mobile';

interface DirectGameBoardProps {
  gameSessionId: string;
  playerColor: 'white' | 'black';
}

export function DirectGameBoard({ gameSessionId, playerColor }: DirectGameBoardProps) {
  const {
    gameSession,
    playerColor: actualColor,
    isLoading,
    error,
    loadTimeout,
    retryLoad,
    eloUpdateResult,
    makeMove,
    resign,
    offerDraw,
    acceptDraw,
    declineDraw,
    rematchState,
    offerRematch,
    acceptRematch,
    declineRematch,
  } = useDirectGameSession(gameSessionId);
  const [selectedSquare, setSelectedSquare] = useState<Position | null>(null);
  const [validMoves, setValidMoves] = useState<Position[]>([]);
  const [showPromotion, setShowPromotion] = useState<{from: Position, to: Position} | null>(null);
  const [lastMovePositions, setLastMovePositions] = useState<{from: Position, to: Position} | null>(null);
  const [showResignConfirm, setShowResignConfirm] = useState(false);
  const [showEloNotification, setShowEloNotification] = useState(false);
  const [showAnalysis, setShowAnalysis] = useState(false);
  const [highlightOpponentMove, setHighlightOpponentMove] = useState(false);
  const { toast } = useToast();
  const { refreshCurrency } = useCurrency();
  const isMobile = useIsMobile();
  const { boardTheme, pieceStyle } = useCosmetics();
  const pieceImages = getPieceImages(pieceStyle as PieceStyle);
  
  const { user } = useAuth();

  // Fetch player profiles
  const playerIds = gameSession ? [gameSession.white_player_id, gameSession.black_player_id] : [];
  const { profiles } = usePlayerProfiles(playerIds);
  
  const whiteProfile = gameSession?.white_player_id ? profiles[gameSession.white_player_id] : null;
  const blackProfile = gameSession?.black_player_id ? profiles[gameSession.black_player_id] : null;

  // Clock management
  const { whiteTimeMs, blackTimeMs, isTimeUp } = useLocalClock(
    gameSession?.time_control || '5+0',
    gameSession?.current_turn || 'white',
    gameSession?.game_status || 'ongoing'
  );

  // Update last move when game session changes and show visual feedback
  useEffect(() => {
    if (gameSession?.last_move) {
      const { from, to } = gameSession.last_move;
      if (from && to) {
        setLastMovePositions({ from, to });
        
        // Highlight opponent's move with visual feedback
        if (actualColor !== gameSession.current_turn) {
          setHighlightOpponentMove(true);
          toast({
            title: "Opponent Moved",
            description: `${from.file}${from.rank} → ${to.file}${to.rank}`,
            duration: 2000,
          });
          setTimeout(() => setHighlightOpponentMove(false), 1000);
        }
      }
    }
  }, [gameSession?.last_move, actualColor, gameSession?.current_turn]);

  // Show ELO notification when game ends and ELO is updated
  useEffect(() => {
    if (eloUpdateResult && user) {
      const playerData = actualColor === 'white' 
        ? eloUpdateResult.white_player 
        : eloUpdateResult.black_player;
      
      setShowEloNotification(true);
      refreshCurrency(); // Refresh currency display
    }
  }, [eloUpdateResult, actualColor, user, refreshCurrency]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-full min-h-[400px]">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  if (error || loadTimeout) {
    return (
      <div className="container mx-auto p-4">
        <Card className="max-w-2xl mx-auto p-8">
          <div className="text-center space-y-6">
            <AlertCircle className="w-16 h-16 mx-auto text-destructive" />
            <div>
              <h2 className="text-2xl font-bold mb-2">Game Not Found</h2>
              <p className="text-muted-foreground">{error || 'Connection timeout'}</p>
            </div>
            
            <div className="flex gap-4 justify-center">
              <Button onClick={retryLoad} variant="outline">
                <RotateCcw className="w-4 h-4 mr-2" />
                Retry
              </Button>
              <Button onClick={() => window.history.back()}>
                <ArrowLeft className="w-4 h-4 mr-2" />
                Return to Menu
              </Button>
            </div>
            
            <Alert>
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>
                This game may have been deleted, expired, or never existed. 
                Try starting a new game from the main menu.
              </AlertDescription>
            </Alert>
          </div>
        </Card>
      </div>
    );
  }

  if (!gameSession) {
    return (
      <div className="flex items-center justify-center h-full min-h-[400px]">
        <Card>
          <CardContent className="pt-6">
            <p className="text-center text-muted-foreground">Game not found</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Normalize game state - will use initial state if game_state is empty/invalid
  const gameState = normalizeGameState(gameSession.game_state);
  const status = getGameStatus(gameState);
  const isPlayerTurn = actualColor === gameSession.current_turn;
  const isGameEnded = gameSession.game_status !== 'ongoing';
  
  // Check if there's a draw offer for this player
  const hasDrawOffer = gameSession.draw_offer_from && gameSession.draw_offer_from !== user?.id;
  const hasOfferedDraw = gameSession.draw_offer_from === user?.id;

  const handleResignClick = () => {
    setShowResignConfirm(true);
  };

  const handleResignConfirm = async () => {
    await resign();
    setShowResignConfirm(false);
  };

  const handleOfferDraw = async () => {
    await offerDraw();
  };

  const handleAcceptDraw = async () => {
    await acceptDraw();
  };

  const handleDeclineDraw = async () => {
    await declineDraw();
  };

  const handleSquareClick = async (position: Position) => {
    if (!gameState || !actualColor) return;

    const [row, col] = position;
    const piece = gameState.board[row][col];

    // Priority 1: Select own pieces
    if (piece && piece.color === actualColor) {
      setSelectedSquare(position);
      const moves = getAllLegalMoves(gameState)
        .filter(move => move.from[0] === row && move.from[1] === col)
        .map(move => move.to);
      setValidMoves(moves);
      return;
    }

    // Priority 2: Execute move if piece selected
    if (selectedSquare) {
      const [selectedRow, selectedCol] = selectedSquare;
      
      // Deselect if clicking same square
      if (row === selectedRow && col === selectedCol) {
        setSelectedSquare(null);
        setValidMoves([]);
        return;
      }

      // Check if valid move
      const isValidMove = validMoves.some(([r, c]) => r === row && c === col);
      
      if (isValidMove) {
        if (!isPlayerTurn) {
          toast({
            title: "Not Your Turn",
            description: "Wait for your opponent to make their move",
            variant: "destructive"
          });
          return;
        }

        // Check for pawn promotion
        const selectedPiece = gameState.board[selectedRow][selectedCol];
        if (selectedPiece?.type === 'P' && (row === 0 || row === 7)) {
          setShowPromotion({ from: selectedSquare, to: position });
          return;
        }

        // Execute move
        const success = await makeMove(selectedSquare, position);
        if (success) {
          setSelectedSquare(null);
          setValidMoves([]);
          setLastMovePositions({ from: selectedSquare, to: position });
        }
      }
    }
  };

  const handlePromotion = async (pieceType: PieceType) => {
    if (!showPromotion) return;
    
    const success = await makeMove(showPromotion.from, showPromotion.to, pieceType);
    if (success) {
      setSelectedSquare(null);
      setValidMoves([]);
      setLastMovePositions(showPromotion);
    }
    setShowPromotion(null);
  };

  // Prepare highlighted squares
  const highlightedSquares: Position[] = [
    ...validMoves,
    ...(lastMovePositions ? [lastMovePositions.from, lastMovePositions.to] : [])
  ];

  // Get player's ELO data for notification
  const playerEloData = eloUpdateResult && user && actualColor ? (
    actualColor === 'white' ? eloUpdateResult.white_player : eloUpdateResult.black_player
  ) : null;

  const isGameStart = gameSession?.move_count === 0 && gameSession?.game_status === 'ongoing';
  const hasRematchOffer = !!gameSession?.rematch_offer_from;
  const isRematchOfferer = hasRematchOffer && gameSession?.rematch_offer_from === user?.id;

  // Mobile Layout
  if (isMobile) {
    return (
      <div className="flex flex-col h-screen safe-area-top safe-area-bottom">
        {/* ELO Update Notification */}
        {playerEloData && (
          <EloNotification
            oldElo={playerEloData.old_elo}
            newElo={playerEloData.new_elo}
            change={playerEloData.elo_change}
            coinsAwarded={playerEloData.coins_awarded}
            gemsAwarded={playerEloData.gems_awarded}
            visible={showEloNotification}
            onClose={() => setShowEloNotification(false)}
          />
        )}

        {/* Top Player Info - Opponent */}
        <div className="flex items-center justify-between px-3 py-3 bg-card border-b">
          <div className="flex items-center gap-2">
            {(actualColor === 'white' ? blackProfile : whiteProfile) && (
              <AvatarDisplay
                username={(actualColor === 'white' ? blackProfile?.username : whiteProfile?.username) || ''}
                avatarConfig={(actualColor === 'white' ? blackProfile?.avatar_config : whiteProfile?.avatar_config) || null}
                avatarUrl={(actualColor === 'white' ? blackProfile?.avatar_url : whiteProfile?.avatar_url) || null}
                size="sm"
              />
            )}
            <div className="flex flex-col">
              <span className="font-medium text-sm">
                {actualColor === 'white' ? blackProfile?.display_name : whiteProfile?.display_name}
              </span>
              <span className="text-xs text-muted-foreground">
                {actualColor === 'white' ? blackProfile?.elo_rating : whiteProfile?.elo_rating}
              </span>
            </div>
          </div>
          <div className={`flex items-center gap-1 font-mono font-bold text-sm ${
            (actualColor === 'white' ? blackTimeMs : whiteTimeMs) < 30000 ? 'text-destructive' : ''
          }`}>
            <Clock className="w-4 h-4" />
            <span>{formatTime(actualColor === 'white' ? blackTimeMs : whiteTimeMs)}</span>
          </div>
        </div>

        {/* Game Status Alerts */}
        {status.status === 'check' && (
          <Alert className="mx-3 mt-2">
            <AlertCircle className="h-4 w-4" />
            <AlertTitle>Check!</AlertTitle>
          </Alert>
        )}

        {isGameEnded && (
          <Alert className="mx-3 mt-2">
            <Trophy className="h-4 w-4" />
            <AlertTitle>Game Over</AlertTitle>
            <AlertDescription>
              {status.status === 'checkmate' && `${status.winner === 'white' ? 'White' : 'Black'} wins!`}
              {status.status === 'stalemate' && 'Draw by stalemate'}
              {status.status === 'draw' && 'Draw'}
            </AlertDescription>
          </Alert>
        )}

        {/* Chess Board - Centered and properly sized */}
        <div className="flex-1 flex items-center justify-center p-2">
          <div className="w-full aspect-square max-h-[calc(100dvh-200px)]">
            <NativeChessBoard
              gameState={gameState}
              onSquareClick={handleSquareClick}
              highlightedSquares={highlightedSquares}
              selectedSquare={selectedSquare}
              flipped={actualColor === 'black'}
              boardTheme={boardTheme}
              pieceStyle={pieceStyle as PieceStyle}
            />
          </div>
        </div>

        {/* Bottom Player Info - You */}
        <div className="flex items-center justify-between px-3 py-3 bg-card border-t">
          <div className="flex items-center gap-2">
            {(actualColor === 'white' ? whiteProfile : blackProfile) && (
              <AvatarDisplay
                username={(actualColor === 'white' ? whiteProfile?.username : blackProfile?.username) || ''}
                avatarConfig={(actualColor === 'white' ? whiteProfile?.avatar_config : blackProfile?.avatar_config) || null}
                avatarUrl={(actualColor === 'white' ? whiteProfile?.avatar_url : blackProfile?.avatar_url) || null}
                size="sm"
              />
            )}
            <div className="flex flex-col">
              <span className="font-medium text-sm">
                {actualColor === 'white' ? whiteProfile?.display_name : blackProfile?.display_name} (You)
              </span>
              <span className="text-xs text-muted-foreground">
                {actualColor === 'white' ? whiteProfile?.elo_rating : blackProfile?.elo_rating}
              </span>
            </div>
          </div>
          <div className={`flex items-center gap-1 font-mono font-bold text-sm ${
            (actualColor === 'white' ? whiteTimeMs : blackTimeMs) < 30000 ? 'text-destructive' : ''
          }`}>
            <Clock className="w-4 h-4" />
            <span>{formatTime(actualColor === 'white' ? whiteTimeMs : blackTimeMs)}</span>
          </div>
        </div>

        {/* Bottom Action Bar */}
        {!isGameEnded && (
          <div className="flex gap-2 px-3 py-2 border-t bg-card">
            <Button 
              variant="destructive" 
              size="sm"
              onClick={handleResignClick}
              className="flex-1"
            >
              Resign
            </Button>
            {!hasOfferedDraw && !hasDrawOffer && (
              <Button 
                variant="outline" 
                size="sm"
                onClick={handleOfferDraw}
                className="flex-1"
              >
                Offer Draw
              </Button>
            )}
          </div>
        )}

        {/* Rematch Bar */}
        {isGameEnded && !hasRematchOffer && !gameSession.rematch_session_id && (
          <div className="px-3 py-2 border-t bg-card">
            <Button 
              variant="default" 
              size="sm"
              onClick={offerRematch}
              className="w-full"
            >
              <RotateCcw className="w-4 h-4 mr-2" />
              Offer Rematch
            </Button>
          </div>
        )}

        {hasRematchOffer && !isRematchOfferer && !gameSession.rematch_session_id && (
          <div className="flex gap-2 px-3 py-2 border-t bg-card">
            <Button 
              variant="default" 
              size="sm"
              onClick={acceptRematch}
              className="flex-1"
            >
              <Check className="w-4 h-4 mr-2" />
              Accept
            </Button>
            <Button 
              variant="outline" 
              size="sm"
              onClick={declineRematch}
              className="flex-1"
            >
              <X className="w-4 h-4 mr-2" />
              Decline
            </Button>
          </div>
        )}

        {/* Dialogs remain the same */}
        <Dialog open={showResignConfirm} onOpenChange={setShowResignConfirm}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Resign Game</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <p className="text-muted-foreground">
                Are you sure you want to resign?
              </p>
              <div className="flex gap-2 justify-end">
                <Button variant="outline" onClick={() => setShowResignConfirm(false)}>
                  Cancel
                </Button>
                <Button variant="destructive" onClick={handleResignConfirm}>
                  Resign
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {showPromotion && (
          <Dialog open={true} onOpenChange={() => setShowPromotion(null)}>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Promote Pawn</DialogTitle>
              </DialogHeader>
              <div className="grid grid-cols-4 gap-4 p-4">
                {(['Q', 'R', 'B', 'N'] as PieceType[]).map(piece => {
                  const pieceKey = actualColor === 'white' ? piece : piece.toLowerCase();
                  const pieceImage = pieceImages[pieceKey];
                  return (
                    <Button
                      key={piece}
                      onClick={() => handlePromotion(piece)}
                      variant="outline"
                      className="h-20 w-20 p-2"
                    >
                      <img src={pieceImage} alt={piece} className="w-full h-full" />
                    </Button>
                  );
                })}
              </div>
            </DialogContent>
          </Dialog>
        )}
      </div>
    );
  }

  // Desktop Layout
  return (
    <div className="space-y-4">
      {/* ELO Update Notification */}
      {playerEloData && (
        <EloNotification
          oldElo={playerEloData.old_elo}
          newElo={playerEloData.new_elo}
          change={playerEloData.elo_change}
          coinsAwarded={playerEloData.coins_awarded}
          gemsAwarded={playerEloData.gems_awarded}
          visible={showEloNotification}
          onClose={() => setShowEloNotification(false)}
        />
      )}

      {/* Connection Status Indicator */}
      <div className="fixed top-4 left-4 z-40">
        <Badge variant="outline" className="bg-background/95 backdrop-blur">
          <Wifi className="h-3 w-3 mr-1 text-success" />
          <span className="text-xs">Connected</span>
        </Badge>
      </div>

      {/* ELO Predictor - Show at game start */}
      {isGameStart && whiteProfile && blackProfile && user && actualColor && (
        <div className="mx-auto max-w-2xl px-4">
          <EloPredictor
            playerElo={actualColor === 'white' ? whiteProfile.elo_rating : blackProfile.elo_rating}
            opponentElo={actualColor === 'white' ? blackProfile.elo_rating : whiteProfile.elo_rating}
            opponentName={actualColor === 'white' ? blackProfile.display_name : whiteProfile.display_name}
          />
        </div>
      )}

      <div className="h-full flex flex-col lg:flex-row gap-4 p-4">
      {/* Player Info Cards */}
      <div className="flex flex-col gap-4 lg:w-72">
        {/* Black Player (top) */}
        <Card className={`border-2 ${gameSession.current_turn === 'black' ? 'border-accent' : 'border-border'}`}>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                {blackProfile && (
                  <AvatarDisplay
                    username={blackProfile.username || blackProfile.display_name}
                    avatarConfig={blackProfile.avatar_config}
                    avatarUrl={blackProfile.avatar_url}
                    size="sm"
                  />
                )}
                <div className="flex flex-col">
                  <div className="flex items-center gap-2">
                    <div className="w-3 h-3 bg-foreground rounded-full"></div>
                    <span className="font-medium text-sm">
                      {blackProfile?.display_name || 'Black Player'}
                      {actualColor === 'black' && ' (You)'}
                    </span>
                  </div>
                  {blackProfile && (
                    <span className="text-xs text-muted-foreground">
                      ELO: {blackProfile.elo_rating}
                    </span>
                  )}
                </div>
              </div>
              <div className={`flex items-center gap-2 p-2 rounded ${
                gameSession.current_turn === 'black' && gameSession.game_status === 'ongoing'
                  ? 'bg-primary/20 text-primary' : 'text-muted-foreground'
              }`}>
                <Clock className="h-4 w-4" />
                <span className={`font-mono text-sm ${blackTimeMs < 30000 ? 'text-destructive' : ''}`}>
                  {formatTime(blackTimeMs)}
                </span>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Game Status */}
        {status.status === 'check' && (
          <Alert>
            <AlertCircle className="h-4 w-4" />
            <AlertTitle>Check!</AlertTitle>
            <AlertDescription>
              {gameSession.current_turn === 'white' ? 'White' : 'Black'} king is in check
            </AlertDescription>
          </Alert>
        )}

        {isGameEnded && (
          <Alert>
            <Trophy className="h-4 w-4" />
            <AlertTitle>Game Over</AlertTitle>
            <AlertDescription>
              {status.status === 'checkmate' && `${status.winner === 'white' ? 'White' : 'Black'} wins by checkmate!`}
              {status.status === 'stalemate' && 'Draw by stalemate'}
              {status.status === 'draw' && `Draw: ${status.reason}`}
            </AlertDescription>
          </Alert>
        )}

        {/* Game Controls */}
        {!isGameEnded && (
          <Card>
            <CardHeader>
              <CardTitle className="text-sm">Game Controls</CardTitle>
            </CardHeader>
            <CardContent className="flex flex-col gap-2">
              <Button 
                variant="destructive" 
                size="sm" 
                onClick={handleResignClick}
              >
                Resign
              </Button>
              {!hasOfferedDraw && !hasDrawOffer && (
                <Button 
                  variant="outline" 
                  size="sm" 
                  onClick={handleOfferDraw}
                >
                  Offer Draw
                </Button>
              )}
              {hasOfferedDraw && (
                <Button variant="outline" size="sm" disabled>
                  Draw Offered
                </Button>
              )}
            </CardContent>
          </Card>
        )}

        {/* Rematch Controls */}
        {isGameEnded && user && (
          <>
            <Card>
              <CardHeader>
                <CardTitle className="text-sm">Rematch</CardTitle>
              </CardHeader>
              <CardContent className="flex flex-col gap-2">
                {!hasRematchOffer && !gameSession.rematch_session_id && (
                  <Button 
                    variant="default" 
                    size="sm" 
                    onClick={offerRematch}
                  >
                    <RotateCcw className="w-4 h-4 mr-2" />
                    Offer Rematch
                  </Button>
                )}
                
                {isRematchOfferer && !gameSession.rematch_session_id && (
                  <Badge variant="secondary" className="justify-center py-2">
                    Waiting for opponent...
                  </Badge>
                )}
                
                {hasRematchOffer && !isRematchOfferer && !gameSession.rematch_session_id && (
                  <>
                    <Button 
                      variant="default" 
                      size="sm" 
                      onClick={acceptRematch}
                    >
                      <Check className="w-4 h-4 mr-2" />
                      Accept Rematch
                    </Button>
                    <Button 
                      variant="outline" 
                      size="sm" 
                      onClick={declineRematch}
                    >
                      <X className="w-4 h-4 mr-2" />
                      Decline
                    </Button>
                  </>
                )}
                
                {gameSession.rematch_session_id && (
                  <Badge variant="default" className="justify-center py-2">
                    Rematch started!
                  </Badge>
                )}
              </CardContent>
            </Card>

            {/* Game Analysis */}
            <Card>
              <CardHeader>
                <CardTitle className="text-sm">Game Analysis</CardTitle>
              </CardHeader>
              <CardContent>
                <Button
                  variant="outline"
                  size="sm"
                  className="w-full"
                  onClick={() => setShowAnalysis(true)}
                >
                  <Activity className="w-4 h-4 mr-2" />
                  View Analysis
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  className="w-full mt-2"
                  onClick={async () => {
                    try {
                      const module = await import('@/hooks/useTrainingPositions');
                      const hook = module.useTrainingPositions();
                      if (gameSessionId) {
                        await hook.extractFromGame(gameSessionId);
                      }
                    } catch (error) {
                      console.error('Error:', error);
                    }
                  }}
                >
                  Extract Training
                </Button>
              </CardContent>
            </Card>
          </>
        )}

        {/* Draw Offer Alert */}
        {hasDrawOffer && !isGameEnded && (
          <Alert className="border-accent">
            <AlertCircle className="h-4 w-4" />
            <AlertTitle>Draw Offer</AlertTitle>
            <AlertDescription>
              Your opponent has offered a draw
            </AlertDescription>
            <div className="flex gap-2 mt-3">
              <Button 
                size="sm" 
                variant="default"
                onClick={handleAcceptDraw}
              >
                Accept
              </Button>
              <Button 
                size="sm" 
                variant="outline"
                onClick={handleDeclineDraw}
              >
                Decline
              </Button>
            </div>
          </Alert>
        )}

        {/* White Player (bottom) */}
        <Card className={`border-2 ${gameSession.current_turn === 'white' ? 'border-accent' : 'border-border'}`}>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                {whiteProfile && (
                  <AvatarDisplay
                    username={whiteProfile.username || whiteProfile.display_name}
                    avatarConfig={whiteProfile.avatar_config}
                    avatarUrl={whiteProfile.avatar_url}
                    size="sm"
                  />
                )}
                <div className="flex flex-col">
                  <div className="flex items-center gap-2">
                    <div className="w-3 h-3 bg-background border-2 border-foreground rounded-full"></div>
                    <span className="font-medium text-sm">
                      {whiteProfile?.display_name || 'White Player'}
                      {actualColor === 'white' && ' (You)'}
                    </span>
                  </div>
                  {whiteProfile && (
                    <span className="text-xs text-muted-foreground">
                      ELO: {whiteProfile.elo_rating}
                    </span>
                  )}
                </div>
              </div>
              <div className={`flex items-center gap-2 p-2 rounded ${
                gameSession.current_turn === 'white' && gameSession.game_status === 'ongoing'
                  ? 'bg-primary/20 text-primary' : 'text-muted-foreground'
              }`}>
                <Clock className="h-4 w-4" />
                <span className={`font-mono text-sm ${whiteTimeMs < 30000 ? 'text-destructive' : ''}`}>
                  {formatTime(whiteTimeMs)}
                </span>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Chess Board */}
      <div className="flex-1 flex items-center justify-center">
        <div className="w-full max-w-2xl">
          <NativeChessBoard
            gameState={gameState}
            onSquareClick={handleSquareClick}
            highlightedSquares={highlightedSquares}
            selectedSquare={selectedSquare}
            flipped={actualColor === 'black'}
            boardTheme={boardTheme}
            pieceStyle={pieceStyle as PieceStyle}
          />
        </div>
      </div>

      {/* Captured Pieces & Info */}
      <div className="hidden lg:block lg:w-72">
        <CapturedPieces gameState={gameState} playerColor={actualColor || 'white'} />
      </div>
      </div>

      {/* Resign Confirmation Dialog */}
      <Dialog open={showResignConfirm} onOpenChange={setShowResignConfirm}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Resign Game</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-muted-foreground">
              Are you sure you want to resign? This will end the game and count as a loss.
            </p>
            <div className="flex gap-2 justify-end">
              <Button 
                variant="outline" 
                onClick={() => setShowResignConfirm(false)}
              >
                Cancel
              </Button>
              <Button 
                variant="destructive" 
                onClick={handleResignConfirm}
              >
                Resign
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Promotion Dialog */}
      {showPromotion && (
        <Dialog open={true} onOpenChange={() => setShowPromotion(null)}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Promote Pawn</DialogTitle>
            </DialogHeader>
            <div className="grid grid-cols-4 gap-4 p-4">
              {(['Q', 'R', 'B', 'N'] as PieceType[]).map(piece => {
                const pieceKey = actualColor === 'white' ? piece : piece.toLowerCase();
                const pieceImage = pieceImages[pieceKey];
                return (
                  <Button
                    key={piece}
                    onClick={() => handlePromotion(piece)}
                    variant="outline"
                    className="h-20 w-20 p-2"
                  >
                    <img src={pieceImage} alt={piece} className="w-full h-full" />
                  </Button>
                );
              })}
            </div>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}
